<template>
    <v-container style="max-width:100vw;">
        <v-toolbar flat class="px-4 pt-3">
            <v-row class="ma-0 pt-6">
                <v-toolbar-title>
                    Ventas del día
                </v-toolbar-title>
                <v-spacer/>
                <v-menu
                    v-model="menu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                >
                    <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                        rounded
                        outlined
                        dense
                        v-model="dateRangeText"
                        label="Rango de fechas"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        style="max-width: 320px"
                    />
                    </template>

                    <v-card>
                    <v-date-picker
                        v-model="dates"
                        range
                        scrollable
                        @change="onDateChange"
                    />
                    <v-card-actions class="pt-0 mt-0">
                        <v-spacer />
                        <v-btn small text @click="menu = false">Cerrar</v-btn>
                        <v-btn small rounded class="elevation-0" color="primary" @click="applyFilter">Aplicar</v-btn>
                    </v-card-actions>
                    </v-card>
                </v-menu>
            </v-row>
        </v-toolbar>
        <div style="background:white!important;" class="px-4 pb-4">
            <!-- Chart -->
            <apexchart
            v-if="series.length"
            type="heatmap"
            height="720"
            :options="chartOptions"
            :series="series"
            />
        </div>
    </v-container>
</template>

<script>
import axios from "axios"

export default {
  data() {
    return {
      loading: false,
      menu: false,

      // Vuetify range picker devuelve [start, end]
      dates: [undefined],

      report: null,
      series: [],

      chartOptions: {
        chart: {
          type: "heatmap",
          toolbar: { show: false }, // quita menú arriba
          zoom: { enabled: false }
        },
        legend: { show: false }, // quita leyenda de rangos arriba

        dataLabels: { enabled: false },

        plotOptions: {
          heatmap: {
            shadeIntensity: 0.5,
            // Para que pinte por sales_count, definimos rangos
            colorScale: {
              ranges: [
                { from: 0, to: 0, name: "0", color: "#f3f4f6" },
                { from: 1, to: 9, name: "1-9", color: "#dbeafe" },
                { from: 10, to: 18, name: "10-18", color: "#93c5fd" },
                { from: 19, to: 27, name: "19-27", color: "#3b82f6" },
                { from: 28, to: 999999, name: ">27", color: "#1d4ed8" }
              ]
            }
          }
        },

        xaxis: {
          type: "category",
          labels: {
            rotate: 0,
            formatter: (val) => {
              // val viene como "00", "01", ... "23"
              const h = Number(val)
              if (Number.isNaN(h)) return val
              const period = h >= 12 ? "PM" : "AM"
              const hh = h % 12 === 0 ? 12 : h % 12
              return `${hh} ${period}`
            }
          }
        },

        yaxis: {
          labels: { style: { fontSize: "12px" } }
        },

        tooltip: {
          // Tooltip custom para mostrar los 3 campos
          custom: function ({ seriesIndex, dataPointIndex, w }) {
            const point =
              w?.globals?.initialSeries?.[seriesIndex]?.data?.[dataPointIndex]

            if (!point) return ""

            const hour = point.x
            const h = Number(hour)
            const period = h >= 12 ? "PM" : "AM"
            const hh = h % 12 === 0 ? 12 : h % 12
            const hourLabel = `${hh} ${period}`

            const date = w?.globals?.seriesNames?.[seriesIndex] || ""
            const sales_count = point?.y ?? 0
            const items_qty = point?.meta?.items_qty ?? 0
            const items_amount = point?.meta?.items_amount ?? 0

            // Formato simple de dinero (ajusta moneda/locale a tu gusto)
            const money = new Intl.NumberFormat("es-MX", {
              style: "currency",
              currency: "MXN"
            }).format(items_amount)

            const ticket_promedio = new Intl.NumberFormat("es-MX", {
              style: "currency",
              currency: "MXN"
            }).format(items_amount/sales_count)

            return `
              <div style="padding:10px; min-width: 220px;">
                <div style="font-weight:600; margin-bottom:6px;">
                  ${date} • ${hourLabel}
                </div>
                <div style="display:flex; justify-content:space-between; gap:12px;">
                  <span>Total</span>
                  <span style="font-weight:600;">${money}</span>
                </div>
                <div style="display:flex; justify-content:space-between; gap:12px;">
                  <span>Cantidad de productos</span>
                  <span style="font-weight:600;">${items_qty}</span>
                </div>
                <div style="display:flex; justify-content:space-between; gap:12px;">
                  <span>Cantidad de ventas</span>
                  <span style="font-weight:600;">${sales_count}</span>
                </div>
                <div style="display:flex; justify-content:space-between; gap:12px;">
                  <span>Ticket Promedio</span>
                  <span style="font-weight:600;">${ticket_promedio}</span>
                </div>
              </div>
            `
          }
        }
      }
    }
  },

  computed: {
    dateRangeText() {
      const [start, end] = this.dates || []
      if (!start && !end) return ""
      if (start && !end) return start
      return `${start} - ${end}`
    }
  },

  created() {
    var date = new Date()
    this.dates = [new Date(date.getFullYear(), date.getMonth(), 1).toLocaleString("sv-SE", {timeZone: "America/Monterrey"}).toString().slice(0, 10), new Date(date.getFullYear(), date.getMonth() + 1, 0).toLocaleString("sv-SE", {timeZone: "America/Monterrey"}).toString().slice(0, 10)]
    this.fetchReport()
  },

  methods: {
    onDateChange(val) {
      // val: [start, end]
      // no hago nada aquí, solo dejo el hook si quieres auto-consultar cuando ya tenga ambas fechas
    },

    applyFilter() {
      this.menu = false
      this.fetchReport()
    },

    async fetchReport() {
      const [start, end] = this.dates || []
      if (!start || !end) return

      this.loading = true
      try {
        const url =
          process.env.VUE_APP_BACKEND_ROUTE +
          "api/v2/reports/sales-heatmap"

        const { data } = await axios.get(url, {
          params: {
            "filter[date_between]": `${start},${end}`
          }
        })

        this.report = data
        this.series = this.buildSeriesFromReport(data)
      } finally {
        this.loading = false
      }
    },

    buildSeriesFromReport(report) {
      // Ejes del API
      const hours = report?.x?.hours || []
      const rows = report?.data || [] // [{date, hours:{...}}]

      // Cada "series" es una fila del heatmap (un día)
      // Cada punto: { x: "08", y: sales_count, meta:{items_amount, items_qty} }
      return rows.map((row) => {
        const day = row.date
        const byHour = row.hours || {}

        const data = hours.map((h) => {
          const cell = byHour?.[h] || {
            sales_count: 0,
            items_qty: 0,
            items_amount: 0
          }

          return {
            x: h,
            y: Number(cell.sales_count || 0),
            meta: {
              items_qty: Number(cell.items_qty || 0),
              items_amount: Number(cell.items_amount || 0)
            }
          }
        })

        return { name: day, data }
      })
    }
  }
}
</script>
